import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  Typography,
} from "@mui/material";

import BreakdownDialogTitle from "../pricing-reports/BreakdownDialogTitle";
import { ink } from "../pricing-reports/design/designTokens";
import ProductImageViewer from "../pricing-reports/ProductImageViewer";
import { formatFlyerGalleryDialogTitle } from "./flyerGalleryDisplay";
import FlyerGalleryDetailMetadata from "./FlyerGalleryDetailMetadata";

function buildGalleryThumbsForDisplayItem(displayItem) {
  const thumbs = [];
  (displayItem?.images || []).forEach((url, imageIndex) => {
    thumbs.push({
      key: `${displayItem.id}:${imageIndex}`,
      url,
      label: `${displayItem.recordAt}`,
      imageIndex,
    });
  });
  return thumbs;
}

export default function FlyerGalleryDetailDialog({ open, displayItem, onClose }) {
  const [selectedThumbKey, setSelectedThumbKey] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedThumbKey("");
    }
  }, [open, displayItem?.id]);

  const galleryDialogTitle = useMemo(
    () => formatFlyerGalleryDialogTitle(displayItem),
    [displayItem]
  );

  const galleryThumbs = useMemo(
    () => (displayItem ? buildGalleryThumbsForDisplayItem(displayItem) : []),
    [displayItem]
  );

  useEffect(() => {
    if (!open) return;
    setSelectedThumbKey(galleryThumbs[0]?.key ?? "");
  }, [open, displayItem?.id, galleryThumbs]);

  const selectedThumb = useMemo(
    () => galleryThumbs.find((thumb) => thumb.key === selectedThumbKey) ?? null,
    [galleryThumbs, selectedThumbKey]
  );

  const viewerImageUrl = selectedThumb?.url ?? "";
  const viewerAlt = displayItem
    ? `${displayItem.product} image ${(selectedThumb?.imageIndex ?? 0) + 1}`
    : "Promotion image";

  return (
    <Dialog className="insights-dialog" open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <BreakdownDialogTitle title={galleryDialogTitle} onClose={onClose} />
      <DialogContent dividers>
        <Stack spacing={2}>
          {!displayItem ? (
            <Alert severity="info">No promotion selected.</Alert>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} lg={7}>
                <Stack spacing={2}>
                  {viewerImageUrl ? (
                    <ProductImageViewer
                      src={viewerImageUrl}
                      alt={viewerAlt}
                      maxHeight={360}
                    />
                  ) : (
                    <Box
                      sx={{
                        minHeight: 240,
                        borderRadius: 1,
                        border: "1px solid #333",
                        backgroundColor: "#000",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography variant="body2" color="grey.400">
                        No image for this promotion
                      </Typography>
                    </Box>
                  )}

                  {galleryThumbs.length > 0 ? (
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: ink }}>
                        Image Gallery
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        sx={{ flexWrap: "wrap", gap: 1 }}
                        role="list"
                        aria-label="Promotion images"
                      >
                        {galleryThumbs.map((thumb) => {
                          const isSelected = selectedThumbKey === thumb.key;
                          return (
                            <Button
                              key={thumb.key}
                              role="listitem"
                              aria-label={thumb.label}
                              aria-pressed={isSelected}
                              variant={isSelected ? "contained" : "outlined"}
                              onClick={() => setSelectedThumbKey(thumb.key)}
                              sx={{
                                flexDirection: "column",
                                alignItems: "stretch",
                                p: 0.75,
                                minWidth: 96,
                                maxWidth: 120,
                                textTransform: "none",
                                color: ink,
                              }}
                            >
                              <Box
                                component="img"
                                src={thumb.url}
                                alt=""
                                sx={{
                                  width: "100%",
                                  height: 72,
                                  objectFit: "cover",
                                  borderRadius: 0.5,
                                  mb: 0.5,
                                  backgroundColor: "#fff",
                                }}
                              />
                              <Typography
                                variant="caption"
                                component="span"
                                sx={{
                                  lineHeight: 1.2,
                                  textAlign: "left",
                                  color: isSelected ? "inherit" : ink,
                                }}
                              >
                                {thumb.label}
                              </Typography>
                            </Button>
                          );
                        })}
                      </Stack>
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No images for this promotion.
                    </Typography>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} lg={5}>
                <FlyerGalleryDetailMetadata item={displayItem} />
              </Grid>
            </Grid>
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
